import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError } from 'rxjs';
import { getConfig } from '../config.service';
import { MiddlewareWebSettings } from '../settings/settings.models';

export interface SeverityLevel {
  id: number;
  title: string;
  color?: string;
  order?: number;
}

export interface DefectState {
  id: number;
  name: string;
}

export interface Category1 {
  id: number;
  name: string;
}

export enum SearchResultKind {
  Building = 0,
  FunctionalArea = 1,
  StaticPoi = 3,
  DeviceRevision = 4,
}

export interface SearchResult {
  title: string;
  description: string;
  sourceKind: SearchResultKind;
  sourceId: number;
  score: number;
  isInNavvisMap: boolean;
  isScanned: boolean;
  sourceTypeId?: number;
  icon?: string;
}

// FMS Location hierarchy models
export interface Building {
  id: number;
  campusId: number;
  code: string;
  name: string;
  description: string;
}

export interface Sector {
  id: number;
  buildingId: number;
  code: string;
  name: string;
}

export interface Floor {
  id: number;
  sectorId: number;
  code: string;
  name: string;
}

export interface Room {
  id: number;
  floorId: number;
  code: string;
  name: string;
  description: string;
  purpose: string;
  area: number;
}

export interface FunctionalArea {
  id: number;
  roomId: number;
  code: string;
  name: string;
  purpose: string;
}

export interface FmsToPoiResult {
  id: number;
  found: boolean;
  isBuildingFallback?: boolean;
}

export interface NavvisSiteSettings {
  defect1PoiTypeId: number;
  defect2PoiTypeId: number;
  defect3PoiTypeId: number;
  outdoorDefectPoiTypeId: number;
  fmsBuildingPoiTypeId: number;
  fmsFunctionalAreaPoiTypeId: number;
  deviceRevisionPoiTypeId: number;
  defaultManagerName?: string;
  defaultManagerEmail?: string;
  defaultCoordinatorName?: string;
  defaultCoordinatorEmail?: string;
}

export interface NavvisSiteInfo {
  id: number;
  name?: string;
  navvisSiteId: number;
  fmsCampusId: number;
  settings?: NavvisSiteSettings;
}

export interface MiddlewareSettings {
  navvisBackendUrl: string;
  siteInfos: NavvisSiteInfo[];
}

/** Entity kinds of the public deep links (/building/{id}, /poi/{id}, ...). */
export type DeepLinkType = 'building' | 'poi' | 'functionalarea' | 'defect';

/** Resolved deep link: the NavVis site to load and the entity to open on it (exactly one is set). */
export interface DeepLinkTarget {
  navvisSiteId: number;
  building?: number | null;
  functionalArea?: number | null;
  poi?: number | null;
  defect?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class MiddlewareApiService {
  private http = inject(HttpClient);
  private get baseUrl() { return getConfig().api.middlewareUrl; }

  /**
   * Get severity levels from middleware (protected endpoint).
   * This demonstrates calling a protected API with Bearer token.
   */
  getSeverityLevels(): Observable<SeverityLevel[]> {
    console.log('[MiddlewareAPI] Calling GET /api/codebook/SeverityLevel');
    return this.http.get<SeverityLevel[]>(`${this.baseUrl}/api/codebook/SeverityLevel`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Get defect states from middleware (protected endpoint).
   */
  getDefectStates(): Observable<DefectState[]> {
    console.log('[MiddlewareAPI] Calling GET /api/codebook/DefectState');
    return this.http.get<DefectState[]>(`${this.baseUrl}/api/codebook/DefectState`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Get category1 list from middleware (protected endpoint).
   */
  getCategory1(): Observable<Category1[]> {
    console.log('[MiddlewareAPI] Calling GET /api/codebook/Category1');
    return this.http.get<Category1[]>(`${this.baseUrl}/api/codebook/Category1`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Search POIs via NavVis/IVION API (protected endpoint).
   * This demonstrates MW calling external IVION API.
   */
  searchPois(siteId: number, term: string): Observable<SearchResult[]> {
    console.log(`[MiddlewareAPI] Calling GET /api/search/${siteId}/Term/${term}`);
    return this.http.get<SearchResult[]>(`${this.baseUrl}/api/search/${siteId}/Term/${term}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error:', error);
        throw error;
      })
    );
  }

  // FMS Location hierarchy endpoints

  /**
   * Get all buildings for a site.
   */
  getBuildings(siteId: number): Observable<Building[]> {
    return this.http.get<Building[]>(`${this.baseUrl}/api/fms/buildings/${siteId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error loading buildings:', error);
        throw error;
      })
    );
  }

  /**
   * Get all sectors for a building.
   */
  getSectors(buildingId: number): Observable<Sector[]> {
    return this.http.get<Sector[]>(`${this.baseUrl}/api/fms/sectors/${buildingId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error loading sectors:', error);
        throw error;
      })
    );
  }

  /**
   * Get all floors for a sector.
   */
  getFloors(sectorId: number): Observable<Floor[]> {
    return this.http.get<Floor[]>(`${this.baseUrl}/api/fms/floors/${sectorId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error loading floors:', error);
        throw error;
      })
    );
  }

  /**
   * Get all rooms for a floor.
   */
  getRooms(floorId: number): Observable<Room[]> {
    return this.http.get<Room[]>(`${this.baseUrl}/api/fms/rooms/${floorId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error loading rooms:', error);
        throw error;
      })
    );
  }

  /**
   * Get all functional areas for a room.
   */
  getFunctionalAreas(roomId: number): Observable<FunctionalArea[]> {
    return this.http.get<FunctionalArea[]>(`${this.baseUrl}/api/fms/functionalAreas/${roomId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error loading functional areas:', error);
        throw error;
      })
    );
  }

  /**
   * Get POI ID for a building to navigate in IVION.
   */
  buildingToPoi(siteId: number, buildingId: number): Observable<FmsToPoiResult> {
    return this.http.get<FmsToPoiResult>(`${this.baseUrl}/api/fms/buildingToPoi/${siteId}/${buildingId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error getting building POI:', error);
        throw error;
      })
    );
  }

  /**
   * Get POI ID for a functional area to navigate in IVION.
   * Falls back to building POI if FA doesn't have its own POI.
   */
  functionalAreaToPoi(siteId: number, faId: number): Observable<FmsToPoiResult> {
    return this.http.get<FmsToPoiResult>(`${this.baseUrl}/api/fms/faToPoiWithBuildingFallback/${siteId}/${faId}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error getting functional area POI:', error);
        throw error;
      })
    );
  }

  getBuildingNamesByLocations(siteId: number, locations: { lon: number; lat: number; z: number }[]): Observable<Record<string, string>> {
    return this.http.post<Record<string, string>>(`${this.baseUrl}/api/fms/buildingNamesByLoc/${siteId}`, locations).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error getting building names by locations:', error);
        throw error;
      })
    );
  }

  /**
   * Get middleware settings including available sites.
   */
  getSettings(): Observable<MiddlewareSettings> {
    return this.http.get<MiddlewareSettings>(`${this.baseUrl}/api/service/Settings`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error getting settings:', error);
        throw error;
      })
    );
  }

  /**
   * Resolve a public deep link (/building/{id}, /poi/{id}, /functionalarea/{id},
   * /defect/{id}) to the site and entity to open. 404 when the entity does not exist.
   */
  resolveDeepLink(type: DeepLinkType, id: number): Observable<DeepLinkTarget> {
    return this.http.get<DeepLinkTarget>(`${this.baseUrl}/api/deeplink/${type}/${id}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error resolving deep link:', type, id, error);
        throw error;
      })
    );
  }

  /**
   * Get web settings including POI type settings, available languages, and user info.
   * This is used by the Settings page.
   */
  getWebSettings(culture = 'en'): Observable<MiddlewareWebSettings> {
    return this.http.get<MiddlewareWebSettings>(`${this.baseUrl}/api/service/WebSettings/${culture}`).pipe(
      catchError((error) => {
        console.error('[MiddlewareAPI] Error getting web settings:', error);
        throw error;
      })
    );
  }
}
